import React, { useState, useEffect, useCallback } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TOM_DEL_SKJEMA = {
  ean: "",
  merke: "",
  navn: "",
  kostpris: "",
  pris: "",
  tilbudspris: "",
  varenummer: "",
  fargenavn: "",
  storrelse: "",
  kategoriSlug: "",
};

export default function Produkter() {
  const [query, setQuery] = useState("");
  const [aktivStatus, setAktivStatus] = useState(""); // "" | "aktive" | "ikke-aktive"
  const [kunPaLager, setKunPaLager] = useState(false);
  const [varegruppe, setVaregruppe] = useState("");
  const [varegrupper, setVaregrupper] = useState([]);
  const [merke, setMerke] = useState("");
  const [merker, setMerker] = useState([]);
  const [oppdatertFra, setOppdatertFra] = useState("");
  const [oppdatertTil, setOppdatertTil] = useState("");
  const [produkter, setProdukter] = useState([]);
  const [søker, setSøker] = useState(false);
  const [oppdatererId, setOppdatererId] = useState(null);
  const [valgte, setValgte] = useState(new Set());
  const [bulkLagrer, setBulkLagrer] = useState(false);
  const [bulkKategoriValg, setBulkKategoriValg] = useState("");
  const [utvidede, setUtvidede] = useState(new Set());
  const [manglerPrisTall, setManglerPrisTall] = useState(null);
  const [antallTall, setAntallTall] = useState(null); // { totalt, aktive, publisert }
  const [manglerFilter, setManglerFilter] = useState(""); // "" | "pris" | "kostpris"
  const [delModalÅpen, setDelModalÅpen] = useState(false);
  const [delSkjema, setDelSkjema] = useState(TOM_DEL_SKJEMA);
  const [delLagrer, setDelLagrer] = useState(false);
  const [delFeil, setDelFeil] = useState("");
  const [redigerModalÅpen, setRedigerModalÅpen] = useState(false);
  const [redigerSkjema, setRedigerSkjema] = useState(null);
  const [redigerLagrer, setRedigerLagrer] = useState(false);
  const [redigerFeil, setRedigerFeil] = useState("");
  const [sortering, setSortering] = useState({ felt: "", retning: 1 }); // retning: 1 stigende, -1 synkende

  useEffect(() => {
    fetch(`${API_BASE_URL}/produkter/kategorier`)
      .then((res) => res.json())
      .then((data) => setVaregrupper(Array.isArray(data) ? data : []))
      .catch(() => setVaregrupper([]));

    fetch(`${API_BASE_URL}/produkter/merker`)
      .then((res) => res.json())
      .then((data) => setMerker(Array.isArray(data) ? data : []))
      .catch(() => setMerker([]));

    fetch(`${API_BASE_URL}/produkter/mangler-pris`)
      .then((res) => res.json())
      .then((data) => setManglerPrisTall(data))
      .catch(() => setManglerPrisTall(null));
  }, []);

  // Tallene over listen: hentes på nytt sammen med listen, så de følger med
  // når produkter aktiveres/publiseres.
  const hentAntall = useCallback(() => {
    fetch(`${API_BASE_URL}/produkter/antall`)
      .then((res) => res.json())
      .then((data) => setAntallTall(typeof data?.totalt === "number" ? data : null))
      .catch(() => setAntallTall(null));
  }, []);

  // Pris-kolonnen: laveste og høyeste gjeldende pris (tilbudspris når den er
  // lavere enn veilpris) over variantene. prisFra brukes til sorteringen.
  function medPrisFelt(p) {
    const gjeldende = (p.varianter || [])
      .map((v) => (typeof v.tilbudspris === "number" && (v.pris == null || v.tilbudspris < v.pris) ? v.tilbudspris : v.pris))
      .filter((x) => typeof x === "number");
    const harTilbud = (p.varianter || []).some((v) => typeof v.tilbudspris === "number" && typeof v.pris === "number" && v.tilbudspris < v.pris);
    return {
      ...p,
      prisFra: gjeldende.length ? Math.min(...gjeldende) : null,
      prisTil: gjeldende.length ? Math.max(...gjeldende) : null,
      harTilbud,
    };
  }

  const hentProdukter = useCallback(async (søketekst, aktivValg, varegruppeSlug, merkeNavn, fraDato, tilDato, mangler, paLager) => {
    const harSøk = søketekst && søketekst.trim();
    if (!harSøk && !aktivValg && !varegruppeSlug && !merkeNavn && !fraDato && !tilDato && !mangler && !paLager) {
      setProdukter([]);
      return;
    }
    setSøker(true);
    try {
      const url = new URL(`${API_BASE_URL}/produkter`);
      if (harSøk) url.searchParams.set("q", søketekst.trim());
      if (aktivValg) url.searchParams.set("aktiv", aktivValg === "aktive" ? "true" : "false");
      if (paLager) url.searchParams.set("paLager", "true");
      if (varegruppeSlug) url.searchParams.set("varegruppe", varegruppeSlug);
      if (merkeNavn) url.searchParams.set("merke", merkeNavn);
      if (fraDato) url.searchParams.set("oppdatertFra", fraDato);
      if (tilDato) url.searchParams.set("oppdatertTil", tilDato);
      if (mangler === "pris") url.searchParams.set("manglerPris", "true");
      if (mangler === "kostpris") url.searchParams.set("manglerKostpris", "true");
      // .toString() - apiAuth.js sitt window.fetch-patch (som legger på ansatt-
      // tokenet) kjenner kun igjen strenger/Request, ikke URL-objekter direkte.
      const res = await fetch(url.toString());
      const data = await res.json();
      setProdukter(Array.isArray(data) ? data.map(medPrisFelt) : []);
      setValgte(new Set());
      setUtvidede(new Set());
    } catch {
      setProdukter([]);
    } finally {
      setSøker(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      hentProdukter(query, aktivStatus, varegruppe, merke, oppdatertFra, oppdatertTil, manglerFilter, kunPaLager);
      hentAntall();
    }, 300);
    return () => clearTimeout(timer);
  }, [query, aktivStatus, varegruppe, merke, oppdatertFra, oppdatertTil, manglerFilter, kunPaLager, hentProdukter, hentAntall]);

  function visManglerFilter(felt) {
    setManglerFilter(felt);
    setQuery("");
    setAktivStatus("");
    setKunPaLager(false);
    setVaregruppe("");
    setMerke("");
    setOppdatertFra("");
    setOppdatertTil("");
  }

  async function endreAktiv(produkt, nyVerdi) {
    setOppdatererId(produkt.id);
    setProdukter((forrige) =>
      forrige.map((p) => (p.id === produkt.id ? { ...p, aktiv: nyVerdi } : p))
    );
    try {
      const res = await fetch(`${API_BASE_URL}/produkter/${produkt.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aktiv: nyVerdi }),
      });
      if (!res.ok) throw new Error("Kunne ikke lagre");
    } catch (err) {
      setProdukter((forrige) =>
        forrige.map((p) => (p.id === produkt.id ? { ...p, aktiv: !nyVerdi } : p))
      );
      alert(err.message);
    } finally {
      setOppdatererId(null);
    }
  }

  function vekslValgt(id) {
    setValgte((forrige) => {
      const ny = new Set(forrige);
      if (ny.has(id)) ny.delete(id);
      else ny.add(id);
      return ny;
    });
  }

  function vekslAlleValgte() {
    setValgte((forrige) =>
      forrige.size === sorterte.length ? new Set() : new Set(sorterte.map((p) => p.id))
    );
  }

  function vekslUtvidet(id) {
    setUtvidede((forrige) => {
      const ny = new Set(forrige);
      if (ny.has(id)) ny.delete(id);
      else ny.add(id);
      return ny;
    });
  }

  function formatKr(beløp) {
    return typeof beløp === "number" ? `${beløp.toLocaleString("no-NO")},-` : "–";
  }

  function formatDato(iso) {
    return iso ? new Date(iso).toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "–";
  }

  async function bulkEndreAktiv(nyVerdi) {
    const ider = [...valgte];
    if (ider.length === 0) return;
    setBulkLagrer(true);
    try {
      const res = await fetch(`${API_BASE_URL}/produkter/bulk`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: ider, aktiv: nyVerdi }),
      });
      if (!res.ok) throw new Error("Kunne ikke oppdatere de valgte produktene");
      setProdukter((forrige) =>
        forrige.map((p) => (valgte.has(p.id) ? { ...p, aktiv: nyVerdi } : p))
      );
      setValgte(new Set());
    } catch (err) {
      alert(err.message);
    } finally {
      setBulkLagrer(false);
    }
  }

  async function bulkFlyttTilKategori(kategoriSlug) {
    const ider = [...valgte];
    if (ider.length === 0 || !kategoriSlug) return;
    const valgtKategori = varegrupper.find((v) => v.slug === kategoriSlug);
    if (!window.confirm(`Flytte ${ider.length} produkt(er) til «${valgtKategori?.navn || kategoriSlug}»?`)) return;
    setBulkLagrer(true);
    try {
      const res = await fetch(`${API_BASE_URL}/produkter/bulk`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: ider, kategoriSlug }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Kunne ikke flytte de valgte produktene");
      setProdukter((forrige) =>
        forrige.map((p) =>
          valgte.has(p.id) ? { ...p, kategori: valgtKategori?.navn || "", kategoriSlug } : p
        )
      );
      setValgte(new Set());
      setBulkKategoriValg("");
    } catch (err) {
      alert(err.message);
    } finally {
      setBulkLagrer(false);
    }
  }

  function åpneDelModal() {
    setDelSkjema(TOM_DEL_SKJEMA);
    setDelFeil("");
    setDelModalÅpen(true);
  }

  function lukkDelModal() {
    if (delLagrer) return;
    setDelModalÅpen(false);
  }

  async function lagreDel(e) {
    e.preventDefault();
    setDelFeil("");
    setDelLagrer(true);
    try {
      const res = await fetch(`${API_BASE_URL}/produkter/deler`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(delSkjema),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Kunne ikke registrere delen");
      setDelModalÅpen(false);
      hentProdukter(query, aktivStatus, varegruppe, merke, oppdatertFra, oppdatertTil, manglerFilter, kunPaLager);
    } catch (err) {
      setDelFeil(err.message);
    } finally {
      setDelLagrer(false);
    }
  }

  function åpneRedigerModal(produkt, variant) {
    setRedigerSkjema({
      produktId: produkt.id,
      variantId: variant.id,
      ean: variant.ean,
      navn: produkt.navn || "",
      merke: produkt.merke || "",
      fargenavn: produkt.fargenavn || "",
      kategoriSlug: produkt.kategoriSlug || "",
      storrelse: variant.storrelse || "",
      artikkelnummer: variant.artikkelnummer || "",
      kostpris: variant.kostpris ?? "",
      pris: variant.pris ?? "",
      tilbudspris: variant.tilbudspris ?? "",
      lagerRoyken: variant.lagerRoyken ?? 0,
      lagerSlemmestad: variant.lagerSlemmestad ?? 0,
    });
    setRedigerFeil("");
    setRedigerModalÅpen(true);
  }

  function lukkRedigerModal() {
    if (redigerLagrer) return;
    setRedigerModalÅpen(false);
  }

  async function lagreRediger(e) {
    e.preventDefault();
    setRedigerFeil("");
    setRedigerLagrer(true);
    try {
      const [produktRes, variantRes] = await Promise.all([
        fetch(`${API_BASE_URL}/produkter/${redigerSkjema.produktId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            navn: redigerSkjema.navn,
            merke: redigerSkjema.merke,
            fargenavn: redigerSkjema.fargenavn,
            kategoriSlug: redigerSkjema.kategoriSlug,
          }),
        }),
        fetch(`${API_BASE_URL}/produkter/varianter/${redigerSkjema.variantId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            storrelse: redigerSkjema.storrelse,
            artikkelnummer: redigerSkjema.artikkelnummer,
            kostpris: redigerSkjema.kostpris,
            pris: redigerSkjema.pris,
            tilbudspris: redigerSkjema.tilbudspris,
            lagerRoyken: redigerSkjema.lagerRoyken,
            lagerSlemmestad: redigerSkjema.lagerSlemmestad,
          }),
        }),
      ]);
      const produktData = await produktRes.json().catch(() => ({}));
      const variantData = await variantRes.json().catch(() => ({}));
      if (!produktRes.ok) throw new Error(produktData.error || "Kunne ikke lagre produktendringer");
      if (!variantRes.ok) throw new Error(variantData.error || "Kunne ikke lagre variantendringer");
      setRedigerModalÅpen(false);
      hentProdukter(query, aktivStatus, varegruppe, merke, oppdatertFra, oppdatertTil, manglerFilter, kunPaLager);
    } catch (err) {
      setRedigerFeil(err.message);
    } finally {
      setRedigerLagrer(false);
    }
  }

  function vekslSortering(felt) {
    setSortering((forrige) =>
      forrige.felt === felt ? { felt, retning: -forrige.retning } : { felt, retning: 1 }
    );
  }

  // Sorterer listen som allerede er hentet. Tomme verdier havner alltid nederst.
  const sorterte = sortering.felt
    ? [...produkter].sort((a, b) => {
        const va = a[sortering.felt];
        const vb = b[sortering.felt];
        const tomA = va === null || va === undefined || va === "";
        const tomB = vb === null || vb === undefined || vb === "";
        if (tomA || tomB) return tomA === tomB ? 0 : tomA ? 1 : -1;
        if (typeof va === "string" && typeof vb === "string" && sortering.felt !== "sisteLagerOppdatering") {
          return va.localeCompare(vb, "no", { numeric: true, sensitivity: "base" }) * sortering.retning;
        }
        const na = sortering.felt === "sisteLagerOppdatering" ? new Date(va).getTime() : Number(va);
        const nb = sortering.felt === "sisteLagerOppdatering" ? new Date(vb).getTime() : Number(vb);
        return (na - nb) * sortering.retning;
      })
    : produkter;

  function sortTh(felt, tekst, align = "left", title) {
    const aktiv = sortering.felt === felt;
    return (
      <th
        key={felt}
        className={`text-${align} px-2 py-2`}
        aria-sort={aktiv ? (sortering.retning === 1 ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          onClick={() => vekslSortering(felt)}
          title={title}
          className={`uppercase inline-flex items-center gap-0.5 hover:text-gray-900 ${aktiv ? "text-gray-900" : ""}`}
        >
          {tekst}
          <span className="text-[10px] w-2">{aktiv ? (sortering.retning === 1 ? "▲" : "▼") : ""}</span>
        </button>
      </th>
    );
  }

  const alleValgt = sorterte.length > 0 && valgte.size === sorterte.length;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h1 className="text-xl font-semibold text-gray-900">Produkter</h1>
        <button
          onClick={åpneDelModal}
          className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-semibold whitespace-nowrap"
        >
          + Legg til del manuelt
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Søk i nettbutikkens produktdatabase, eller vis alt som er aktivt. Huk av Aktiv for å gjøre
        et produkt synlig i denne listen for videre redigering — offentlig synlighet i nettbutikken
        styres separat, med Publiser-knappen i redigeringsmodalen på produktsiden.
      </p>

      {manglerPrisTall && (manglerPrisTall.manglerPris > 0 || manglerPrisTall.manglerKostpris > 0) && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          {manglerPrisTall.manglerPris > 0 && (
            <button
              onClick={() => visManglerFilter(manglerFilter === "pris" ? "" : "pris")}
              className={`hover:underline ${manglerFilter === "pris" ? "font-semibold underline" : ""}`}
            >
              ⚠ {manglerPrisTall.manglerPris} produkt(er) mangler pris
            </button>
          )}
          {manglerPrisTall.manglerKostpris > 0 && (
            <button
              onClick={() => visManglerFilter(manglerFilter === "kostpris" ? "" : "kostpris")}
              className={`hover:underline ${manglerFilter === "kostpris" ? "font-semibold underline" : ""}`}
            >
              ⚠ {manglerPrisTall.manglerKostpris} produkt(er) mangler kostpris
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-3">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setManglerFilter(""); }}
          placeholder="Søk: EAN, varenummer, artikkelnummer, produkt, merke eller kategori …"
          className="min-w-0 flex-1 sm:basis-64 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={varegruppe}
          onChange={(e) => { setVaregruppe(e.target.value); setManglerFilter(""); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700"
        >
          <option value="">Alle varegrupper</option>
          {varegrupper.map((v) => (
            <option key={v.slug} value={v.slug}>
              {"   ".repeat(v.dybde)}{v.navn}
            </option>
          ))}
        </select>
        <select
          value={merke}
          onChange={(e) => { setMerke(e.target.value); setManglerFilter(""); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700"
        >
          <option value="">Alle merker</option>
          {merker.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <div className="flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap" title="Filtrerer på når en import sist skrev noe på produktet (lager, pris osv.) — ikke manuell redigering i denne siden">
          <span className="text-gray-400">Importert</span>
          <input
            type="date"
            value={oppdatertFra}
            onChange={(e) => { setOppdatertFra(e.target.value); setManglerFilter(""); }}
            className="border border-gray-300 rounded-lg px-2 py-2 text-sm text-gray-700"
          />
          <span className="text-gray-400">–</span>
          <input
            type="date"
            value={oppdatertTil}
            onChange={(e) => { setOppdatertTil(e.target.value); setManglerFilter(""); }}
            className="border border-gray-300 rounded-lg px-2 py-2 text-sm text-gray-700"
          />
          {(oppdatertFra || oppdatertTil) && (
            <button
              type="button"
              onClick={() => { setOppdatertFra(""); setOppdatertTil(""); }}
              className="text-gray-400 hover:text-gray-700 text-xs underline"
            >
              Nullstill
            </button>
          )}
        </div>
        <select
          value={aktivStatus}
          onChange={(e) => { setAktivStatus(e.target.value); setManglerFilter(""); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700"
          aria-label="Aktiv"
        >
          <option value="">Aktive og ikke aktive</option>
          <option value="aktive">Kun aktive</option>
          <option value="ikke-aktive">Kun ikke aktive</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={kunPaLager}
            onChange={(e) => { setKunPaLager(e.target.checked); setManglerFilter(""); }}
          />
          Kun på lager
        </label>
      </div>

      {valgte.size > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <span className="text-sm text-gray-700">{valgte.size} valgt</span>
          <button
            onClick={() => bulkEndreAktiv(true)}
            disabled={bulkLagrer}
            className="px-2.5 py-1 rounded-lg bg-gray-900 text-white text-xs font-semibold disabled:opacity-50"
          >
            Merk som aktive
          </button>
          <button
            onClick={() => bulkEndreAktiv(false)}
            disabled={bulkLagrer}
            className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-white disabled:opacity-50"
          >
            Fjern aktiv
          </button>
          <span className="w-px h-5 bg-gray-300" />
          <select
            value={bulkKategoriValg}
            onChange={(e) => setBulkKategoriValg(e.target.value)}
            disabled={bulkLagrer}
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-700"
          >
            <option value="">Flytt til kategori …</option>
            {varegrupper.map((v) => (
              <option key={v.slug} value={v.slug}>
                {"   ".repeat(v.dybde)}{v.navn}
              </option>
            ))}
          </select>
          <button
            onClick={() => bulkFlyttTilKategori(bulkKategoriValg)}
            disabled={bulkLagrer || !bulkKategoriValg}
            className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-white disabled:opacity-50"
          >
            Flytt
          </button>
        </div>
      )}

      {(() => {
        const harFilter = !!(query.trim() || aktivStatus || kunPaLager || varegruppe || merke || oppdatertFra || oppdatertTil || manglerFilter);
        const tall = (n) => (typeof n === "number" ? n.toLocaleString("no-NO") : "…");
        const fulltSøk = query.trim() && produkter.length === 50;
        return (
          <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2 text-sm text-gray-600">
            <span>Totalt: <b className="text-gray-900">{tall(antallTall?.totalt)}</b> produkter</span>
            <span>Aktive: <b className="text-gray-900">{tall(antallTall?.aktive)}</b></span>
            <span>Publisert i nettbutikken: <b className="text-gray-900">{tall(antallTall?.publisert)}</b></span>
            {harFilter && (
              <span>
                Filtrert: <b className="text-gray-900">{søker ? "…" : `${tall(produkter.length)}${fulltSøk ? "+" : ""}`}</b>
              </span>
            )}
          </div>
        );
      })()}

      <div className="border border-gray-200 rounded-xl overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-2 py-2">
                <input
                  type="checkbox"
                  checked={alleValgt}
                  onChange={vekslAlleValgte}
                  disabled={sorterte.length === 0}
                />
              </th>
              <th className="px-2 py-2"></th>
              {sortTh("varenummer", "Varenummer")}
              {sortTh("navn", "Produkt")}
              {sortTh("fargenavn", "Farge")}
              {sortTh("merke", "Merke")}
              {sortTh("kategori", "Kategori")}
              {sortTh("prisFra", "Pris", "right", "Laveste gjeldende pris (tilbud når lavere)")}
              {sortTh("totalLager", "Lager", "right")}
              {sortTh("sisteLagerOppdatering", "Oppdatert", "left", "Sist lageroppdatering")}
              {sortTh("publisert", "Publ.", "center", "Publisert i nettbutikken")}
              {sortTh("aktiv", "Aktiv", "center")}
            </tr>
          </thead>
          <tbody>
            {sorterte.map((p) => {
              const utvidet = utvidede.has(p.id);
              return (
                <React.Fragment key={p.id}>
                  <tr
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => vekslUtvidet(p.id)}
                  >
                    <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={valgte.has(p.id)} onChange={() => vekslValgt(p.id)} />
                    </td>
                    <td className="px-2 py-2 text-center text-gray-400">{utvidet ? "▾" : "▸"}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{p.varenummer}</td>
                    <td className="px-2 py-2">{p.navn}</td>
                    <td className="px-2 py-2">{p.fargenavn}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{p.merke}</td>
                    <td className="px-2 py-2">{p.kategori}</td>
                    <td
                      className={`px-2 py-2 text-right whitespace-nowrap ${p.harTilbud ? "text-red-600" : ""}`}
                      title={p.harTilbud ? "Har tilbudspris" : undefined}
                    >
                      {p.prisFra == null
                        ? "–"
                        : p.prisTil !== p.prisFra
                          ? `${p.prisFra.toLocaleString("no-NO")}–${p.prisTil.toLocaleString("no-NO")},-`
                          : formatKr(p.prisFra)}
                    </td>
                    <td className="px-2 py-2 text-right">{p.totalLager}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-gray-500">{formatDato(p.sisteLagerOppdatering)}</td>
                    <td className="px-2 py-2 text-center">
                      {p.publisert ? (
                        <span className="text-emerald-600 text-xs font-medium">Ja</span>
                      ) : (
                        <span className="text-gray-400 text-xs">Nei</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={p.aktiv}
                        disabled={oppdatererId === p.id}
                        onChange={(e) => endreAktiv(p, e.target.checked)}
                      />
                    </td>
                  </tr>
                  {utvidet && (
                    <tr className="border-t border-gray-100 bg-gray-50/60">
                      <td></td>
                      <td colSpan={11} className="px-3 py-3">
                        {p.varianter.length === 0 ? (
                          <p className="text-xs text-gray-400">Ingen varianter.</p>
                        ) : (
                          <table className="text-xs w-full max-w-lg">
                            <thead className="text-gray-400 uppercase">
                              <tr>
                                <th className="text-left pr-4 py-1">EAN</th>
                                <th className="text-left pr-4 py-1">Størrelse</th>
                                <th className="text-right pr-4 py-1">Antall</th>
                                <th className="text-right pr-4 py-1">Kostpris</th>
                                <th className="text-right pr-4 py-1">Veilpris</th>
                                <th className="text-right py-1">Tilbudspris</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.varianter.map((v) => (
                                <tr
                                  key={v.ean}
                                  className="border-t border-gray-100 hover:bg-gray-100 cursor-pointer"
                                  onClick={() => åpneRedigerModal(p, v)}
                                  title="Trykk for å redigere denne varen"
                                >
                                  <td className="pr-4 py-1 whitespace-nowrap">{v.ean}</td>
                                  <td className="pr-4 py-1 whitespace-nowrap">{v.storrelse || "–"}</td>
                                  <td className="pr-4 py-1 text-right">{v.antall}</td>
                                  <td className="pr-4 py-1 text-right">{formatKr(v.kostpris)}</td>
                                  <td className="pr-4 py-1 text-right">{formatKr(v.pris)}</td>
                                  <td className="py-1 text-right">{formatKr(v.tilbudspris)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!søker && (query.trim() || aktivStatus || kunPaLager || varegruppe || manglerFilter) && produkter.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-gray-400 text-sm">
                  Ingen treff.
                </td>
              </tr>
            )}
            {!query.trim() && !aktivStatus && !kunPaLager && !varegruppe && !manglerFilter && (
              <tr>
                <td colSpan={12} className="px-3 py-6 text-center text-gray-400 text-sm">
                  Skriv i søkefeltet, velg en varegruppe, eller velg «Kun aktive».
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {query.trim() && produkter.length === 50 && (
        <p className="text-xs text-gray-400 mt-2">Viser de første 50 treffene. Snevre inn søket for flere.</p>
      )}

      {delModalÅpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={lukkDelModal}
        >
          <div
            className="bg-white w-full max-w-lg rounded-xl shadow p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-gray-900">Legg til del manuelt</h2>
              <button onClick={lukkDelModal} className="text-gray-500 hover:text-black text-sm">
                Lukk
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              For deler som ikke kommer med i backoffice-eksporten og dermed ikke kan importeres.
              Oppretter et nytt produkt med én variant, deaktivert — som ved vanlig import.
            </p>
            <form onSubmit={lagreDel} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-semibold text-gray-700">
                  EAN *
                  <input
                    type="text"
                    required
                    value={delSkjema.ean}
                    onChange={(e) => setDelSkjema((s) => ({ ...s, ean: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-700">
                  Merke *
                  <input
                    type="text"
                    required
                    value={delSkjema.merke}
                    onChange={(e) => setDelSkjema((s) => ({ ...s, merke: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                  />
                </label>
              </div>

              <label className="block text-xs font-semibold text-gray-700">
                Produktnavn *
                <input
                  type="text"
                  required
                  value={delSkjema.navn}
                  onChange={(e) => setDelSkjema((s) => ({ ...s, navn: e.target.value }))}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-semibold text-gray-700">
                  Kostpris *
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={delSkjema.kostpris}
                    onChange={(e) => setDelSkjema((s) => ({ ...s, kostpris: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-700">
                  Pris (veilpris) *
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={delSkjema.pris}
                    onChange={(e) => setDelSkjema((s) => ({ ...s, pris: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                  />
                </label>
              </div>

              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer select-none font-semibold text-gray-600">
                  Valgfrie felt
                </summary>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <label className="text-xs font-semibold text-gray-700">
                    Tilbudspris
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={delSkjema.tilbudspris}
                      onChange={(e) => setDelSkjema((s) => ({ ...s, tilbudspris: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-700">
                    Varenummer
                    <input
                      type="text"
                      value={delSkjema.varenummer}
                      onChange={(e) => setDelSkjema((s) => ({ ...s, varenummer: e.target.value }))}
                      placeholder="Standard: samme som EAN"
                      className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-700">
                    Farge
                    <input
                      type="text"
                      value={delSkjema.fargenavn}
                      onChange={(e) => setDelSkjema((s) => ({ ...s, fargenavn: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-700">
                    Størrelse
                    <input
                      type="text"
                      value={delSkjema.storrelse}
                      onChange={(e) => setDelSkjema((s) => ({ ...s, storrelse: e.target.value }))}
                      placeholder="Standard: O/S"
                      className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                    />
                  </label>
                  <label className="col-span-2 text-xs font-semibold text-gray-700">
                    Kategori
                    <select
                      value={delSkjema.kategoriSlug}
                      onChange={(e) => setDelSkjema((s) => ({ ...s, kategoriSlug: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-700"
                    >
                      <option value="">Ingen kategori</option>
                      {varegrupper.map((v) => (
                        <option key={v.slug} value={v.slug}>
                          {"   ".repeat(v.dybde)}{v.navn}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </details>

              {delFeil && <p className="text-xs text-red-600">{delFeil}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={lukkDelModal}
                  disabled={delLagrer}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={delLagrer}
                  className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {delLagrer ? "Lagrer …" : "Legg til"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {redigerModalÅpen && redigerSkjema && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={lukkRedigerModal}
        >
          <div
            className="bg-white w-full max-w-lg rounded-xl shadow p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold text-gray-900">Rediger vare</h2>
              <button onClick={lukkRedigerModal} className="text-gray-500 hover:text-black text-sm">
                Lukk
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">EAN {redigerSkjema.ean} — EAN kan ikke endres.</p>
            <form onSubmit={lagreRediger} className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Produktinformasjon (gjelder alle størrelser/varianter av dette produktet)
                </p>
                <div className="space-y-3">
                  <label className="block text-xs font-semibold text-gray-700">
                    Produktnavn
                    <input
                      type="text"
                      required
                      value={redigerSkjema.navn}
                      onChange={(e) => setRedigerSkjema((s) => ({ ...s, navn: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs font-semibold text-gray-700">
                      Merke
                      <input
                        type="text"
                        value={redigerSkjema.merke}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, merke: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-700">
                      Farge
                      <input
                        type="text"
                        value={redigerSkjema.fargenavn}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, fargenavn: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                  </div>
                  <label className="block text-xs font-semibold text-gray-700">
                    Kategori
                    <select
                      value={redigerSkjema.kategoriSlug}
                      onChange={(e) => setRedigerSkjema((s) => ({ ...s, kategoriSlug: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-700"
                    >
                      <option value="">Ingen kategori</option>
                      {varegrupper.map((v) => (
                        <option key={v.slug} value={v.slug}>
                          {"   ".repeat(v.dybde)}{v.navn}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Denne varen (EAN {redigerSkjema.ean})
                </p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs font-semibold text-gray-700">
                      Størrelse
                      <input
                        type="text"
                        required
                        value={redigerSkjema.storrelse}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, storrelse: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-700">
                      Artikkelnummer
                      <input
                        type="text"
                        value={redigerSkjema.artikkelnummer}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, artikkelnummer: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <label className="text-xs font-semibold text-gray-700">
                      Kostpris
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={redigerSkjema.kostpris}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, kostpris: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-700">
                      Pris *
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={redigerSkjema.pris}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, pris: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-700">
                      Tilbudspris
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={redigerSkjema.tilbudspris}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, tilbudspris: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs font-semibold text-gray-700">
                      Lager Røyken
                      <input
                        type="number"
                        min="0"
                        value={redigerSkjema.lagerRoyken}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, lagerRoyken: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs font-semibold text-gray-700">
                      Lager Slemmestad
                      <input
                        type="number"
                        min="0"
                        value={redigerSkjema.lagerSlemmestad}
                        onChange={(e) => setRedigerSkjema((s) => ({ ...s, lagerSlemmestad: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {redigerFeil && <p className="text-xs text-red-600">{redigerFeil}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={lukkRedigerModal}
                  disabled={redigerLagrer}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={redigerLagrer}
                  className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {redigerLagrer ? "Lagrer …" : "Lagre"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
